/**
 * slider.js — Alpine.js slider component (Slick-style API).
 *
 * Extracted from snippets/slider.liquid so the ~44 KB of component code is a
 * cacheable CDN asset instead of an inline <script> duplicated once per
 * {% render 'slider' %} (the homepage carried four copies — ~180 KB of HTML).
 *
 * snippets/slider.liquid still owns:
 *   - the scoped <style> block
 *   - the window.GJSlider config bridge (translated aria-labels + arrow icon
 *     SVGs — the only parts that need Liquid rendering)
 *   - the <script src> tag that loads this file
 *
 * Guard: the snippet may render (and therefore emit the script tag) multiple
 * times per page; the browser fetches once but executes once per tag, so the
 * whole file is wrapped in a run-once guard — same pattern as pdp-spec.js.
 */
if (!window.__gjSliderLoaded) {
  window.__gjSliderLoaded = true;

  /* Liquid-rendered labels/icons — seeded by snippets/slider.liquid before this
     deferred script runs. Fallbacks keep the slider functional standalone. */
  const gjSliderBridge = window.GJSlider ?? {};
  const cfg = {
    i18n: {
      previousSlide: 'Previous slide',
      nextSlide: 'Next slide',
      goToSlide: 'Go to slide {{ number }}',
      ...gjSliderBridge.i18n,
    },
    icons: { prev: '', next: '', ...gjSliderBridge.icons },
  };

  // Global event delegation for external slider arrows
  // One listener handles all data-slider-prev/next buttons — no timing issues
  if (!window._sliderDelegationBound) {
    document.addEventListener('click', (e) => {
      const prev = e.target.closest('[data-slider-prev]');
      if (prev) {
        const api = document.getElementById(prev.getAttribute('data-slider-prev'))?._sliderApi;
        if (api) api.prev();
        return;
      }
      const next = e.target.closest('[data-slider-next]');
      if (next) {
        const api = document.getElementById(next.getAttribute('data-slider-next'))?._sliderApi;
        if (api) api.next();
      }
    });
    window._sliderDelegationBound = true;
  }

  document.addEventListener('alpine:init', () => {
    // Skip slider registration entirely when no slider exists on the page (unless in Theme Editor)
    if (
      !document.querySelector('[x-data*="slider("]') &&
      !document.documentElement.classList.contains('shopify-design-mode')
    )
      return;

    const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

    Alpine.data('slider', (userOpts = {}) => ({
      defaults: {
        slidesToShow: 1,
        slidesToScroll: 1,
        startIndex: 0,
        infinite: true,
        dots: false,
        arrows: true,
        arrowStyle: 'default',
        draggable: false,
        scrollbar: false,
        scrollbarClass: '',
        autoplay: false,
        autoplaySpeed: 9000,
        pauseOnHover: true,
        speed: 400,
        spaceBetween: 0,
        paddingLeft: 0,
        alignInactive: 'left',
        easing: 'ease',
        responsive: [],
        onInit: null,
        onChange: null,
        thumbnails: {
          enabled: false,
          selector: 'img',
          getSrc: null,
          size: 80,
          position: 'below',
        },
        animate: true,
        clipViewport: true,
      },

      opts: {},

      width: 0,
      slideWidth: 0,
      slideCount: 0,

      current: 0,
      physicalIndex: 0,
      animating: false,

      pointerDown: false,
      startX: 0,
      deltaX: 0,

      isDraggingScrollbar: false,
      scrollbarStartX: 0,

      timer: null,
      fallbackTimer: null,
      _cleanupFn: null,
      _deltaXResetTimer: null,
      $viewport: null,
      $track: null,
      $scrollbar: null,
      $thumb: null,
      $slides: [],
      $clonesBefore: [],
      $clonesAfter: [],
      $dots: null,
      $thumbs: null,
      $prevBtn: null,
      $nextBtn: null,

      init() {
        if (this.$root.__sliderMounted) return;

        // Lazy-mount. A single page can carry many sliders (the homepage has
        // ~10). Running every slider's heavy mount() — DOM cloning, per-slide
        // width writes and height measurement — during Alpine's init pass piles
        // all of it into one long task plus a cascade of forced reflows, which
        // is the dominant Total Blocking Time cost. Instead we mount only the
        // sliders at/near the viewport right away (hero, plus anything an
        // explicit init() call targets — cart-drawer upsells, the gallery
        // lightbox — which are visible by the time they fire) and defer the rest
        // until the user scrolls them into range, staggering the work.
        const designMode = document.documentElement.classList.contains('shopify-design-mode');
        if (designMode || typeof IntersectionObserver === 'undefined' || this._isNearViewport()) {
          this.mount();
          return;
        }

        const io = new IntersectionObserver(
          (entries, obs) => {
            if (entries.some((e) => e.isIntersecting)) {
              obs.disconnect();
              this.mount();
            }
          },
          { rootMargin: '200px 0px' },
        );
        io.observe(this.$root);
      },

      _isNearViewport() {
        const rect = this.$root.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        // Zero-size means not laid out yet or display:none (e.g. a slider in a
        // closed drawer/modal) — let the observer wait for it to be shown rather
        // than mounting it blind.
        if (rect.width === 0 && rect.height === 0) return false;
        return rect.top < vh + 200 && rect.bottom > -200;
      },

      mount() {
        if (this.$root.__sliderMounted) return;
        this.$root.__sliderMounted = true;

        this.opts = { ...this.defaults, ...userOpts };
        this.applyResponsive();

        // Always enable draggable on mobile for touch swiping
        if (window.innerWidth < 1024) {
          this.opts.draggable = true;
        }

        this.createStructure();

        // Register all event listeners unconditionally — even an inactive slider
        // must respond to breakpoint changes and gain drag on resize
        window.addEventListener('resize', this.onResize.bind(this));
        window.addEventListener('mousemove', this.globalMouseMove.bind(this));
        window.addEventListener('touchmove', this.globalMouseMove.bind(this), { passive: false });
        window.addEventListener('mouseup', this.globalMouseUp.bind(this));
        window.addEventListener('touchend', this.globalMouseUp.bind(this));
        window.addEventListener('touchcancel', this.globalMouseUp.bind(this));
        this.$viewport.addEventListener('mousedown', this.startDrag.bind(this));
        this.$viewport.addEventListener('touchstart', this.startDrag.bind(this), { passive: true });
        this.$viewport.addEventListener('keydown', this.onKey.bind(this));
        this.$viewport.addEventListener(
          'click',
          (e) => {
            if (e.target.closest('[data-slider-ui]')) return;
            if (Math.abs(this.deltaX) >= 10) {
              e.preventDefault();
              e.stopPropagation();
            }
          },
          true,
        );

        if (this.slideCount <= this.opts.slidesToShow) {
          this.$root.classList.add('slider-inactive');
          this.updateDimensions();
          this.bindExternalArrows(true);

          if (this.$track) {
            this.$track.style.transform = 'none';
            this.$track.style.justifyContent = this.opts.alignInactive === 'center' ? 'center' : 'flex-start';
          }
          return;
        }

        this.current = clamp(this.opts.startIndex, 0, this.slideCount - 1);
        this.physicalIndex = this.current;

        if (this.opts.infinite && this.slideCount > this.opts.slidesToShow) {
          this.buildClones();
        }

        this.updateDimensions();

        this.goTo(this.current, false);

        // Re-trigger updateHeight once images load (they may report offsetHeight 0 on init).
        // Batch through scheduleHeight so a burst of images finishing in the same
        // frame collapses into a single measure+write instead of one reflow each.
        this.$slides.forEach((slide) => {
          slide.querySelectorAll('img').forEach((img) => {
            if (!img.complete) {
              img.addEventListener('load', () => this.scheduleHeight(), { once: true });
            }
          });
        });

        this.buildDots();
        this.buildArrows();
        this.bindExternalArrows();
        this.updateArrows();
        this.buildThumbs();
        this.buildScrollbar();

        if (this.opts.pauseOnHover) {
          this.$root.addEventListener('mouseenter', () => this.stopAutoplay());
          this.$root.addEventListener('mouseleave', () => this.startAutoplay());
        }

        this.startAutoplay();

        this.$root._sliderApi = this.api();
        if (this.opts.onInit) this.opts.onInit(this.$root._sliderApi);
        this.$root.dispatchEvent(
          new CustomEvent('slider:init', { detail: { el: this.$root, api: this.$root._sliderApi }, bubbles: true }),
        );
      },

      api() {
        return {
          el: this.$root,
          next: () => this.next(),
          prev: () => this.prev(),
          goTo: (i, animate = true) => this.goTo(i, animate),
          getCurrent: () => this.current,
          getOptions: () => this.opts,
        };
      },

      get $root() {
        return this.$el;
      },

      globalMouseMove(e) {
        if (this.pointerDown) this.onDrag(e);
        if (this.isDraggingScrollbar) this.onScrollbarDrag(e);
      },

      globalMouseUp(e) {
        if (this.pointerDown) this.endDrag(e);
        if (this.isDraggingScrollbar) this.endScrollbarDrag(e);
      },

      buildScrollbar() {
        if (!this.opts.scrollbar) return;

        if (this.$scrollbar) this.$scrollbar.remove();

        const track = document.createElement('div');
        track.className = `slider-scrollbar relative w-auto h-1 bg-black/20 rounded-full cursor-pointer overflow-hidden touch-none ${this.opts.scrollbarClass}`;
        track.setAttribute('data-slider-ui', '');
        this.$root.appendChild(track);

        const thumb = document.createElement('div');
        thumb.className = 'absolute top-0 left-0 h-full color-scheme-3 bg-background rounded-full';
        thumb.style.transition = `transform ${this.opts.speed}ms ${this.opts.easing}`;
        track.appendChild(thumb);

        this.$scrollbar = track;
        this.$thumb = thumb;

        track.addEventListener('mousedown', (e) => {
          if (e.target === thumb) return;
          this.handleScrollbarClick(e);
        });

        thumb.addEventListener('mousedown', this.startScrollbarDrag.bind(this));
        thumb.addEventListener('touchstart', this.startScrollbarDrag.bind(this));

        this.updateScrollbar();
      },

      handleScrollbarClick(e) {
        const rect = this.$scrollbar.getBoundingClientRect();
        const percent = clamp((e.clientX - rect.left) / rect.width, 0, 1);
        const maxIndex = Math.max(0, this.slideCount - this.opts.slidesToShow);
        const target = (() => {
          const raw = percent * maxIndex;
          const rounded = Math.round(raw);
          if (rounded > maxIndex || Math.abs(maxIndex - raw) < Math.abs(rounded - raw)) return maxIndex;
          return clamp(rounded, 0, maxIndex);
        })();
        this.goTo(target, true);
      },

      startScrollbarDrag(e) {
        e.preventDefault();
        e.stopPropagation();
        this.isDraggingScrollbar = true;
        this.stopAutoplay();
        this.$thumb.style.transition = 'none';
        this.$track.style.transitionDuration = '0ms';
      },

      onScrollbarDrag(e) {
        if (!this.isDraggingScrollbar || !this.$scrollbar) return;
        e.preventDefault();

        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const rect = this.$scrollbar.getBoundingClientRect();
        const offsetX = clientX - rect.left;

        let percent = clamp(offsetX / rect.width, 0, 1);

        const thumbWidthPercent = parseFloat(this.$thumb.style.width) || 0;
        const maxThumbMoveOfParent = 100 - thumbWidthPercent;

        if (this.opts.infinite) {
          // Map thumb position to a fractional slide index (0 to last slide)
          const floatIdx = percent * (this.slideCount - 1);
          const trackTranslate = this.getTrackTranslateValue(floatIdx);
          this.$track.style.transform = `translate3d(${trackTranslate}px, 0, 0)`;
          // Store for endScrollbarDrag
          this._scrollbarDragIdx = floatIdx;
        } else {
          const maxIndex = Math.max(0, this.slideCount - this.opts.slidesToShow);
          const floatIdx = percent * maxIndex;
          const trackTranslate = this.getTrackTranslateValue(floatIdx);
          this.$track.style.transform = `translate3d(${trackTranslate}px, 0, 0)`;
          this._scrollbarDragIdx = floatIdx;
        }

        const moveInParentPercent = percent * maxThumbMoveOfParent;
        const transformPercent = thumbWidthPercent > 0 ? moveInParentPercent * (100 / thumbWidthPercent) : 0;

        this.$thumb.style.transform = `translate3d(${transformPercent}%, 0, 0)`;
      },

      endScrollbarDrag() {
        if (!this.isDraggingScrollbar) return;
        this.isDraggingScrollbar = false;
        if (this.$thumb) this.$thumb.style.transition = `transform ${this.opts.speed}ms ${this.opts.easing}`;
        this.$track.style.transitionDuration = this.opts.speed + 'ms';

        const floatIdx = this._scrollbarDragIdx != null ? this._scrollbarDragIdx : 0;
        const maxIndex = Math.max(0, this.slideCount - this.opts.slidesToShow);
        let targetIdx = Math.round(floatIdx);
        targetIdx = clamp(targetIdx, 0, this.opts.infinite ? this.slideCount - 1 : maxIndex);

        this._scrollbarDragIdx = null;
        this.goTo(targetIdx, true);
        this.startAutoplay();
      },

      updateScrollbar() {
        if (!this.$thumb || !this.$scrollbar) return;

        if (this.opts.infinite) {
          // Infinite mode: one discrete segment per slide
          // Thumb = 1/slideCount, moves one segment per slide, wraps to start
          const thumbWidthPercent = (1 / this.slideCount) * 100;
          this.$thumb.style.width = `${thumbWidthPercent}%`;

          const normalizedCurrent = ((this.current % this.slideCount) + this.slideCount) % this.slideCount;
          // Each slide position = one segment; translate is in thumb-widths
          const transformPercent = normalizedCurrent * 100;

          this.$thumb.style.transform = `translate3d(${transformPercent}%, 0, 0)`;
          return;
        }

        // Non-infinite mode: original logic
        const maxIndex = Math.max(0, this.slideCount - this.opts.slidesToShow);
        const maxTranslate = Math.abs(this.getTrackTranslateValue(maxIndex));

        if (maxTranslate <= 1) {
          this.$thumb.style.width = '100%';
          this.$thumb.style.transform = 'translate3d(0,0,0)';
          return;
        }

        const totalEffectiveWidth = maxTranslate + this.width;
        const thumbWidthPercent = (this.width / totalEffectiveWidth) * 100;

        this.$thumb.style.width = `${thumbWidthPercent}%`;

        const currentTranslate = Math.abs(this.getTrackTranslateValue(this.current));

        let progress = currentTranslate / maxTranslate;

        if (this.current >= maxIndex - 0.1) progress = 1;
        if (this.current <= 0.1) progress = 0;

        progress = Math.max(0, Math.min(1, progress));

        const maxThumbMoveOfParent = 100 - thumbWidthPercent;
        const moveInParentPercent = progress * maxThumbMoveOfParent;

        const transformPercent = moveInParentPercent * (100 / thumbWidthPercent);

        this.$thumb.style.transform = `translate3d(${transformPercent}%, 0, 0)`;
      },

      startDrag(e) {
        if (!this.opts.draggable || this.animating) return;
        if (this.$root.classList.contains('slider-inactive')) return;

        e.stopPropagation();

        if (this._deltaXResetTimer) {
          clearTimeout(this._deltaXResetTimer);
          this._deltaXResetTimer = null;
        }
        this.deltaX = 0;

        if (e.type === 'mousedown') {
          e.preventDefault();
        }

        this.pointerDown = true;

        this.startX = e.touches ? e.touches[0].clientX : e.clientX;

        this.$track.style.transitionDuration = '0ms';
        this.stopAutoplay();
      },

      onDrag(e) {
        if (!this.pointerDown) return;

        const x = e.touches ? e.touches[0].clientX : e.clientX;
        this.deltaX = x - this.startX;

        // Clamp drag at the edges of a finite slider so the user
        // can't pull past the first slide or the last visible group.
        if (!this.opts.infinite) {
          const atStart = this.current === 0;
          const lastSnap = Math.max(0, this.slideCount - Math.floor(this.opts.slidesToShow));
          const atEnd = this.current >= lastSnap;
          if (atStart && this.deltaX > 0) this.deltaX = 0;
          if (atEnd && this.deltaX < 0) this.deltaX = 0;
        }

        if (e.type === 'touchmove' && e.cancelable) {
          if (Math.abs(this.deltaX) >= 10) {
            e.preventDefault();
          }
        }

        const baseTranslate = this.getTrackTranslateValue(this.physicalIndex);
        const currentTranslate = baseTranslate + this.deltaX;

        this.$track.style.transform = `translate3d(${currentTranslate}px, 0, 0)`;

        // Sync scrollbar thumb position during drag
        if (this.$thumb && this.$scrollbar) {
          const totalUnitWidth = this.slideWidth + this.opts.spaceBetween;
          const cloneOffset = this.opts.infinite ? this.$clonesBefore.length * totalUnitWidth : 0;
          const rawTranslate = -currentTranslate;
          const floatIdx = (rawTranslate - cloneOffset) / totalUnitWidth;
          const normalizedIdx = ((floatIdx % this.slideCount) + this.slideCount) % this.slideCount;

          const thumbWidthPercent = parseFloat(this.$thumb.style.width) || 0;

          if (this.opts.infinite) {
            // Infinite: position based on normalized slide index
            const transformPercent = normalizedIdx * 100;
            this.$thumb.style.transition = 'none';
            this.$thumb.style.transform = `translate3d(${transformPercent}%, 0, 0)`;
          } else {
            // Finite: position based on progress through max range
            const maxIndex = Math.max(0, this.slideCount - this.opts.slidesToShow);
            const progress = maxIndex > 0 ? clamp(floatIdx / maxIndex, 0, 1) : 0;
            const maxThumbMove = 100 - thumbWidthPercent;
            const transformPercent = thumbWidthPercent > 0 ? (progress * maxThumbMove * 100) / thumbWidthPercent : 0;
            this.$thumb.style.transition = 'none';
            this.$thumb.style.transform = `translate3d(${transformPercent}%, 0, 0)`;
          }
        }
      },

      endDrag(_e) {
        if (!this.pointerDown) return;
        this.pointerDown = false;

        // Restore scrollbar thumb transition for the snap animation
        if (this.$thumb) {
          this.$thumb.style.transition = `transform ${this.opts.speed}ms ${this.opts.easing}`;
        }

        if (Math.abs(this.deltaX) < 10) {
          this.goTo(this.physicalIndex, true);
          this.startAutoplay();
          this.deltaX = 0;
          return;
        }

        const totalUnitWidth = this.slideWidth + this.opts.spaceBetween;
        const draggedUnits = -this.deltaX / totalUnitWidth;
        let moveCount = Math.round(draggedUnits);

        if (moveCount === 0) {
          if (draggedUnits > 0.2) moveCount = 1;
          else if (draggedUnits < -0.2) moveCount = -1;
        }

        let target = Math.round(this.physicalIndex) + moveCount;

        if (!this.opts.infinite) {
          const maxIndex = Math.max(0, this.slideCount - this.opts.slidesToShow);
          target = clamp(target, 0, maxIndex);
        }

        this.goTo(target, true);
        this.startAutoplay();

        this._deltaXResetTimer = setTimeout(() => {
          this._deltaXResetTimer = null;
          this.deltaX = 0;
        }, 350);
      },

      getTrackTranslateValue(index) {
        return -(this.getEffectiveIndex(index) * (this.slideWidth + this.opts.spaceBetween));
      },

      createStructure() {
        const root = this.$root;

        // If the slider structure was pre-rendered in Liquid (for LCP),
        // just bind refs and bail – no DOM movement needed.
        const existingViewport = root.querySelector(':scope > .slider-viewport');
        if (existingViewport) {
          this.$viewport = existingViewport;
          this.$track = existingViewport.querySelector('.slider-track');
          if (this.opts.draggable) existingViewport.style.cursor = 'grab';
          existingViewport.setAttribute('tabindex', '0');
          root.classList.add('flex', 'flex-col', 'gap-3');

          // Suppress native drag on images & links, same as the normal path
          this.$track.querySelectorAll('img, a').forEach((el) => el.setAttribute('draggable', 'false'));

          // Bind same refs that the normal path creates
          this.$slides = Array.from(this.$track.children);
          this.slideCount = this.$slides.length;

          if (this.opts.spaceBetween > 0) {
            this.$track.style.gap = `${this.opts.spaceBetween}px`;
          }
          if (this.opts.paddingLeft > 0) {
            this.$track.style.paddingLeft = `${this.opts.paddingLeft}px`;
          }

          const dots = document.createElement('div');
          dots.className = 'slider-dots flex items-center justify-center gap-2 absolute bottom-0 w-full';
          dots.setAttribute('data-slider-ui', '');
          this.$dots = dots;
          if (this.opts.dots) root.appendChild(dots);
          return;
        }

        const children = Array.from(root.children).filter((n) => {
          if (n.nodeType !== 1) return false;
          if (n.hasAttribute('x-ignore')) return false;
          if (n.hasAttribute('data-slider-ui')) return false;
          if (n.classList.contains('slider-viewport')) return false;
          return true;
        });

        // Clear any pre-render grid styles (used to prevent CLS before JS)
        root.style.display = '';
        root.style.gridTemplateColumns = '';
        root.style.gap = '';
        // Remove the scoped <style> block that sets the pre-render grid
        if (root.id) {
          const preStyle = root.parentElement && root.parentElement.querySelector('style');
          if (preStyle && preStyle.textContent.includes('#' + root.id)) {
            preStyle.remove();
          }
        }
        root.classList.add('flex', 'flex-col', 'gap-3');

        const viewport = document.createElement('div');
        viewport.className = `slider-viewport relative ${this.opts.clipViewport ? 'overflow-hidden' : ''} focus:outline-none min-w-0`;
        viewport.setAttribute('tabindex', '0');
        if (this.opts.draggable) viewport.style.cursor = 'grab';

        this.$viewport = viewport;

        const track = document.createElement('div');
        track.className = 'slider-track flex items-stretch transition-transform will-change-transform';

        if (this.opts.spaceBetween > 0) {
          track.style.gap = `${this.opts.spaceBetween}px`;
        }
        if (this.opts.paddingLeft > 0) {
          track.style.paddingLeft = `${this.opts.paddingLeft}px`;
        }

        this.$track = track;

        children.forEach((child) => {
          const wrap = document.createElement('div');
          wrap.className = 'slider-slide flex flex-col shrink-0 select-none';
          child.querySelectorAll('img, a').forEach((el) => el.setAttribute('draggable', 'false'));
          wrap.appendChild(child);
          track.appendChild(wrap);
        });

        viewport.appendChild(track);
        root.appendChild(viewport);

        const dots = document.createElement('div');
        dots.className = 'slider-dots flex items-center justify-center gap-2 absolute bottom-0 w-full';
        dots.setAttribute('data-slider-ui', '');
        this.$dots = dots;
        if (this.opts.dots) root.appendChild(dots);

        this.$slides = Array.from(track.children);
        this.slideCount = this.$slides.length;

        // Re-initialise Alpine on moved children so x-show / x-data
        // bindings that were disconnected during DOM restructuring
        if (typeof Alpine !== 'undefined') {
          queueMicrotask(() => {
            this.$slides.forEach((slide) => {
              slide.querySelectorAll('[x-data]').forEach((el) => {
                Alpine.destroyTree(el);
                Alpine.initTree(el);
              });
            });
          });
        }
      },

      buildClones() {
        this.destroyClones();

        const n = Math.ceil(this.opts.slidesToShow);
        const slides = this.$slides;
        const count = slides.length;

        const startFragment = document.createDocumentFragment();
        const endFragment = document.createDocumentFragment();

        for (let i = 0; i < n; i++) {
          const index = (((count - n + i) % count) + count) % count;
          const clone = slides[index].cloneNode(true);
          clone.classList.add('slider-clone');
          this.$clonesBefore.push(clone);
          startFragment.appendChild(clone);
        }

        for (let i = 0; i < n; i++) {
          const clone = slides[i % count].cloneNode(true);
          clone.classList.add('slider-clone');
          this.$clonesAfter.push(clone);
          endFragment.appendChild(clone);
        }

        this.$track.insertBefore(startFragment, this.$track.firstChild);
        this.$track.appendChild(endFragment);

        // Remove loading/fetchpriority from clone images to avoid
        // interfering with LCP discovery — clones placed before real
        // slides appear first in DOM order.
        this.$root.querySelectorAll('.slider-clone img').forEach((img) => {
          img.setAttribute('loading', 'lazy');
          img.removeAttribute('fetchpriority');
        });

        // Strip x-cloak so CSS `[x-cloak] { display: none }` doesn't hide clones
        // (Alpine won't process clones inserted after init)
        this.$root.querySelectorAll('.slider-clone[x-cloak]').forEach((el) => el.removeAttribute('x-cloak'));
        this.$root.querySelectorAll('.slider-clone [x-cloak]').forEach((el) => el.removeAttribute('x-cloak'));
      },

      destroyClones() {
        this.$root.querySelectorAll('.slider-clone').forEach((el) => el.remove());
        this.$clonesBefore = [];
        this.$clonesAfter = [];
      },

      applyResponsive() {
        if (!Array.isArray(this.opts.responsive) || !this.opts.responsive.length) return;
        const width = window.innerWidth;

        const sorted = [...this.opts.responsive].sort((a, b) => a.breakpoint - b.breakpoint);

        sorted.forEach((r) => {
          if (width >= r.breakpoint) {
            this.opts = { ...this.opts, ...r.settings };
          }
        });
      },

      updateDimensions() {
        this.width = this.$viewport.getBoundingClientRect().width;

        // Guard: if the viewport hasn't been laid out yet (width 0), defer
        // until the browser has painted. Prevents slides rendering at 0px.
        if (this.width === 0) {
          requestAnimationFrame(() => this.updateDimensions());
          return;
        }

        const gapsVisible = this.opts.slidesToShow - 1;
        const totalGapSpace = Math.max(0, gapsVisible * this.opts.spaceBetween);
        const leftPad = this.opts.paddingLeft || 0;

        this.slideWidth = (this.width - leftPad - totalGapSpace) / this.opts.slidesToShow;

        if (this.opts.spaceBetween >= 0) {
          this.$track.style.gap = `${this.opts.spaceBetween}px`;
        }

        const allSlides = Array.from(this.$track.children);
        allSlides.forEach((s) => (s.style.width = this.slideWidth + 'px'));

        if (this.opts.scrollbar && !this.$scrollbar) {
          this.buildScrollbar();
        } else {
          this.updateScrollbar();
        }

        // Defer the offsetHeight read out of this write phase (we just set every
        // slide's width) so it doesn't force a synchronous reflow mid-mount.
        this.scheduleHeight();
      },

      _heightRaf: null,

      scheduleHeight() {
        if (this._heightRaf) return;
        this._heightRaf = requestAnimationFrame(() => {
          this._heightRaf = null;
          this.updateHeight();
        });
      },

      updateHeight() {
        if (this.$root.classList.contains('no-height-js')) {
          this.$viewport.style.height = '';
          return;
        }
        const effectiveIndex = this.getEffectiveIndex(this.current);
        const from = Math.floor(effectiveIndex);
        const to = Math.ceil(effectiveIndex + this.opts.slidesToShow);
        const all = Array.from(this.$track.children);
        let maxH = 0;
        for (let i = from; i < to && i < all.length; i++) {
          if (all[i]) maxH = Math.max(maxH, all[i].offsetHeight);
        }
        if (maxH > 0) this.$viewport.style.height = maxH + 'px';

        if (this.$thumbs && (this.opts.thumbnails?.position === 'left' || this.opts.thumbnails?.position === 'right')) {
          this.$thumbs.style.maxHeight = this.$viewport.clientHeight + 'px';
        }
      },

      getEffectiveIndex(idx) {
        return this.opts.infinite ? idx + this.$clonesBefore.length : idx;
      },

      next() {
        this.slideBy(this.opts.slidesToScroll);
      },
      prev() {
        this.slideBy(-this.opts.slidesToScroll);
      },

      slideBy(delta) {
        // We purposely omit 'if (this.animating) return;' here so rapid clicks
        // interrupt the current CSS transition and trigger the next slide without dropping inputs.
        let target = Math.round(this.physicalIndex) + delta;
        if (!this.opts.infinite) {
          const maxIndex = Math.max(0, this.slideCount - this.opts.slidesToShow);
          target = clamp(target, 0, maxIndex);
        } else {
          const n = Math.ceil(this.opts.slidesToShow);
          target = clamp(target, -n, this.slideCount + n - 1);
        }
        this.goTo(target, true);
      },

      goTo(index, animate = true) {
        if (this.animating && this._cleanupFn) {
          this.$track.removeEventListener('transitionend', this._cleanupFn);
          if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
          this._cleanupFn = null;
        }

        this.animating = true;

        const count = this.slideCount;
        const prevCurrent = this.current;

        this.physicalIndex = index;
        this.current = ((index % count) + count) % count;

        this.updateThumbsActive();
        this.updateDots();
        this.updateScrollbar();
        this.updateArrows();

        const pixelTranslate = this.getTrackTranslateValue(index);

        this.$root.dispatchEvent(
          new CustomEvent('slider:change', {
            detail: { el: this.$root, from: prevCurrent, current: this.current, api: this.api() },
            bubbles: true,
          }),
        );
        if (this.opts.onChange) this.opts.onChange(this.current, this.api());

        const shouldAnimate = animate && this.opts.animate;

        this.$track.style.transitionDuration = shouldAnimate ? this.opts.speed + 'ms' : '0ms';
        this.$track.style.transitionTimingFunction = this.opts.easing;
        this.$track.style.transform = `translate3d(${pixelTranslate}px, 0, 0)`;

        const cleanup = (e) => {
          if (e && e.type === 'transitionend' && e.target !== this.$track) return;
          this.$track.removeEventListener('transitionend', cleanup);
          if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
          this._cleanupFn = null;

          if (this.opts.infinite) {
            if (index < 0 || index >= count) {
              this.physicalIndex = this.current;

              this.$track.style.transitionDuration = '0ms';
              this.$track.offsetHeight;

              const snapTranslate = this.getTrackTranslateValue(this.current);
              this.$track.style.transform = `translate3d(${snapTranslate}px, 0, 0)`;
              this.$track.offsetHeight;
            }
          }

          this.animating = false;
          this.updateHeight();
        };
        this._cleanupFn = cleanup;

        if (shouldAnimate && this.opts.speed > 0) {
          this.$track.addEventListener('transitionend', cleanup);
          this.fallbackTimer = setTimeout(cleanup, this.opts.speed + 50);
        } else {
          cleanup();
        }
      },

      buildArrows() {
        if (!this.opts.arrows) return;
        let prevBtn = this.$root.querySelector('.slider-prev');
        let nextBtn = this.$root.querySelector('.slider-next');

        if (!prevBtn) {
          prevBtn = document.createElement('button');
          prevBtn.type = 'button';
          prevBtn.setAttribute('data-slider-ui', '');
          prevBtn.setAttribute('aria-label', cfg.i18n.previousSlide);
          if (this.opts.arrowStyle == 'simple') {
            prevBtn.className =
              'slider-prev cursor-pointer absolute left-0 top-1/2 -translate-y-1/2 focus:outline-none z-10';
          } else {
            prevBtn.className =
              'slider-prev btn-icon btn-secondary-icon cursor-pointer absolute left-0 top-1/2 -translate-y-1/2 focus:outline-none z-10';
          }
          prevBtn.innerHTML = cfg.icons.prev;
          prevBtn.addEventListener('click', () => {
            if (this.opts.autoplay) {
              this.stopAutoplay();
              this.startAutoplay();
            }
            this.prev();
          });
          this.$viewport.appendChild(prevBtn);
        }
        if (!nextBtn) {
          nextBtn = document.createElement('button');
          nextBtn.type = 'button';
          nextBtn.setAttribute('data-slider-ui', '');
          nextBtn.setAttribute('aria-label', cfg.i18n.nextSlide);
          if (this.opts.arrowStyle == 'simple') {
            nextBtn.className =
              'slider-next cursor-pointer absolute right-0 top-1/2 -translate-y-1/2 focus:outline-none z-10';
          } else {
            nextBtn.className =
              'slider-next btn-icon btn-secondary-icon cursor-pointer absolute right-0 top-1/2 -translate-y-1/2 focus:outline-none z-10';
          }
          nextBtn.innerHTML = cfg.icons.next;
          nextBtn.addEventListener('click', () => {
            if (this.opts.autoplay) {
              this.stopAutoplay();
              this.startAutoplay();
            }
            this.next();
          });
          this.$viewport.appendChild(nextBtn);
        }

        if (prevBtn) prevBtn.setAttribute('data-slider-ui', '');
        if (nextBtn) nextBtn.setAttribute('data-slider-ui', '');

        this.$prevBtn = prevBtn;
        this.$nextBtn = nextBtn;

        this.updateArrows();
      },

      updateArrows() {
        const isInfinite = this.opts.infinite;
        const maxIndex = Math.max(0, this.slideCount - this.opts.slidesToShow);
        const disablePrev = !isInfinite && this.current <= 0;
        const disableNext = !isInfinite && this.current >= maxIndex;

        if (this.opts.arrows) {
          this.$root.classList.remove('slider-no-arrows');
        } else {
          this.$root.classList.add('slider-no-arrows');
        }

        // Built-in arrows
        if (this.$prevBtn) this.$prevBtn.disabled = disablePrev;
        if (this.$nextBtn) this.$nextBtn.disabled = disableNext;

        // External arrows: any element with data-slider-prev="<this slider's id>"
        const id = this.$root.id;
        if (id) {
          document.querySelectorAll(`[data-slider-prev="${id}"]`).forEach((el) => (el.disabled = disablePrev));
          document.querySelectorAll(`[data-slider-next="${id}"]`).forEach((el) => (el.disabled = disableNext));
        }
      },

      bindExternalArrows(inactive) {
        const id = this.$root.id;
        if (!id) return;

        const prevEls = document.querySelectorAll(`[data-slider-prev="${id}"]`);
        const nextEls = document.querySelectorAll(`[data-slider-next="${id}"]`);

        if (inactive) {
          prevEls.forEach((el) => {
            el.style.display = 'none';
            if (el.parentElement) el.parentElement.style.display = 'none';
          });
          nextEls.forEach((el) => {
            el.style.display = 'none';
            if (el.parentElement) el.parentElement.style.display = 'none';
          });
          return;
        }

        // Show arrows (in case previously hidden by inactive state)
        prevEls.forEach((el) => {
          el.style.display = '';
          if (el.parentElement) el.parentElement.style.display = '';
        });
        nextEls.forEach((el) => {
          el.style.display = '';
          if (el.parentElement) el.parentElement.style.display = '';
        });
        // Click binding is handled by the global delegation listener — no per-element binding needed
      },

      getPageCount() {
        const maxIndex = Math.max(0, this.slideCount - this.opts.slidesToShow);
        return Math.max(1, Math.ceil(maxIndex / this.opts.slidesToScroll) + 1);
      },

      buildDots() {
        if (!this.opts.dots) return;

        const maxIndex = Math.max(0, this.slideCount - this.opts.slidesToShow);
        const pages = this.getPageCount();

        this.$dots.innerHTML = '';

        for (let i = 0; i < pages; i++) {
          const b = document.createElement('button');
          b.type = 'button';
          b.className =
            'cursor-pointer slider-dot size-2.5 rounded-full bg-gray-300 aria-[current=true]:bg-gray-900 transition';
          b.setAttribute('aria-current', i === 0 ? 'true' : 'false');

          b.addEventListener('click', () => {
            let target = i * this.opts.slidesToScroll;
            if (!this.opts.infinite && target > maxIndex) target = maxIndex;
            this.goTo(target, true);
            if (this.opts.autoplay) {
              this.stopAutoplay();
              this.startAutoplay();
            }
          });

          if (this.opts.autoplay && this.opts.pauseOnHover) {
            const pause = () => this.stopAutoplay();
            const resume = () => this.startAutoplay();
            ['mouseenter', 'pointerenter', 'focusin'].forEach((evt) => b.addEventListener(evt, pause));
            ['mouseleave', 'pointerleave', 'focusout'].forEach((evt) => b.addEventListener(evt, resume));
          }

          const wrap = document.createElement('div');
          wrap.className = 'p-1';
          wrap.appendChild(b);
          this.$dots.appendChild(wrap);
        }
      },

      updateDots() {
        if (!this.opts.dots) return;
        let activePage = Math.round(this.current / this.opts.slidesToScroll);

        const pages = this.getPageCount();
        if (activePage >= pages) activePage = pages - 1;

        const dots = this.$dots.querySelectorAll('.slider-dot');
        dots.forEach((d, i) => d.setAttribute('aria-current', i === activePage ? 'true' : 'false'));
      },

      buildThumbs() {
        if (!this.opts.thumbnails?.enabled) return;
        if (this.$thumbs) {
          this.$thumbs.remove();
          this.$thumbs = null;
        }

        const pos = this.opts.thumbnails.position || 'below';
        const size = this.opts.thumbnails.size || 80;

        this.$root.classList.remove('thumbs-left', 'thumbs-right', 'thumbs-above', 'thumbs-below');
        this.$root.classList.add(`thumbs-${pos}`);

        const wrap = document.createElement('div');
        wrap.className = 'slider-thumbs';
        wrap.setAttribute('data-slider-ui', '');
        wrap.classList.add('flex', 'gap-2');

        this.$viewport.classList.remove('order-1', 'order-2', 'order-3', 'flex-1', 'min-w-0');
        wrap.classList.remove(
          'order-1',
          'order-2',
          'order-3',
          'overflow-x-auto',
          'overflow-y-auto',
          'flex-row',
          'flex-col',
          'shrink-0',
        );

        if (this.$dots) this.$dots.classList.remove('order-1', 'order-2', 'order-3', 'order-4', 'w-full', 'basis-full');

        if (pos === 'left' || pos === 'right') {
          this.$root.classList.remove('flex-col');
          this.$root.classList.add('flex-row', 'items-start', 'flex-wrap');
          this.$viewport.classList.add('flex-1', 'min-w-0');
          wrap.classList.add('flex-col', 'shrink-0', 'overflow-y-auto');
          wrap.style.width = size + 'px';
          wrap.style.maxHeight = '';
          if (pos === 'left') {
            wrap.classList.add('order-1');
            this.$viewport.classList.add('order-2');
          } else {
            this.$viewport.classList.add('order-1');
            wrap.classList.add('order-2');
          }
          if (this.$dots) this.$dots.classList.add('order-3', 'w-full', 'basis-full');
        } else {
          this.$root.classList.remove('flex-row');
          this.$root.classList.add('flex-col');
          this.$viewport.classList.add('order-2', 'min-w-0');
          wrap.classList.add('flex-row', 'overflow-x-auto');
          if (pos === 'above') {
            wrap.classList.add('order-1');
            if (this.$dots) this.$dots.classList.add('order-3', 'w-full');
          } else {
            wrap.classList.add('order-3');
            if (this.$dots) this.$dots.classList.add('order-4', 'w-full');
          }
        }

        if (pos === 'above') {
          this.$root.insertBefore(wrap, this.$viewport);
        } else if (pos === 'left') {
          this.$root.insertBefore(wrap, this.$viewport);
        } else if (pos === 'right' || pos === 'below') {
          this.$root.appendChild(wrap);
        }

        this.$slides.forEach((slideEl, i) => {
          let src = null;
          const thumbNode = slideEl.querySelector('[data-thumb-src]');
          if (thumbNode) src = thumbNode.getAttribute('data-thumb-src');
          if (!src && typeof this.opts.thumbnails.getSrc === 'function') {
            src = this.opts.thumbnails.getSrc(slideEl, i);
          }
          if (!src) {
            const img = slideEl.querySelector(this.opts.thumbnails.selector || 'img');
            if (img) src = img.getAttribute('src') || img.src || img.currentSrc || null;
          }
          if (!src) return;

          const btn = document.createElement('button');
          btn.type = 'button';
          let ariaLabel = cfg.i18n.goToSlide;
          if (ariaLabel.includes('{{ number }}')) {
            ariaLabel = ariaLabel.replace('{{ number }}', String(i + 1));
          } else {
            ariaLabel = `${ariaLabel} ${i + 1}`;
          }
          btn.setAttribute('aria-label', ariaLabel);
          btn.setAttribute('data-thumb-index', String(i));
          btn.setAttribute('data-slider-ui', '');
          Object.assign(btn.style, {
            width: size + 'px',
            height: size + 'px',
            flex: '0 0 auto',
            borderRadius: '0',
            overflow: 'hidden',
            border: '2px solid transparent',
            padding: '0',
            cursor: 'pointer',
            background: 'transparent',
          });

          const imgEl = document.createElement('img');
          imgEl.src = src;
          imgEl.alt = '';
          imgEl.loading = 'lazy';
          imgEl.decoding = 'async';
          imgEl.width = size;
          imgEl.height = size;
          Object.assign(imgEl.style, {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          });

          btn.addEventListener('click', () => {
            this.goTo(i, true);
          });
          wrap.appendChild(btn);
          btn.appendChild(imgEl);
        });

        this.$thumbs = wrap;
        this.updateThumbsActive();

        this.$nextTick(() => {
          if (pos === 'left' || pos === 'right') {
            this.$thumbs.style.maxHeight = this.$viewport.clientHeight + 'px';
          }
        });
      },

      updateThumbsActive() {
        if (!this.$thumbs) return;
        const buttons = this.$thumbs.querySelectorAll('[data-thumb-index]');
        buttons.forEach((b) => {
          const thumbIndex = parseInt(b.getAttribute('data-thumb-index'), 10);
          const isActive = thumbIndex === this.current;
          b.setAttribute('aria-current', isActive ? 'true' : 'false');
          b.style.borderColor = isActive ? '#111827' : 'transparent';
          if (isActive) b.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        });
      },

      startAutoplay() {
        if (!this.opts.autoplay) return;
        this.stopAutoplay();
        this.timer = setInterval(() => this.next(), this.opts.autoplaySpeed);
      },
      stopAutoplay() {
        if (this.timer) {
          clearInterval(this.timer);
          this.timer = null;
        }
      },

      onKey(e) {
        if (e.key === 'ArrowRight') this.next();
        if (e.key === 'ArrowLeft') this.prev();
      },

      _resizeTimer: null,

      onResize() {
        // Debounce: only execute after resizing has settled for 150ms
        if (this._resizeTimer) clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => this._doResize(), 150);
      },

      _doResize() {
        this.opts = { ...this.defaults, ...userOpts };
        this.applyResponsive();

        // Always enable draggable on mobile for touch swiping
        if (window.innerWidth < 1024) {
          this.opts.draggable = true;
        }

        const wasInactive = this.$root.classList.contains('slider-inactive');
        const isNowInactive = this.slideCount <= this.opts.slidesToShow;

        if (isNowInactive) {
          // Slider can no longer scroll at this breakpoint
          if (!wasInactive) {
            this.stopAutoplay();
            this.destroyClones();
            this.$root.classList.add('slider-inactive');
          }
          this.updateDimensions();
          this.bindExternalArrows(true);
          // Remove scrollbar when inactive
          if (this.$scrollbar) {
            this.$scrollbar.remove();
            this.$scrollbar = null;
            this.$thumb = null;
          }
          if (this.$track) {
            this.$track.style.transform = 'none';
            this.$track.style.justifyContent = this.opts.alignInactive === 'center' ? 'center' : 'flex-start';
          }
          return;
        }

        // Slider is (or becomes) active
        if (wasInactive) {
          this.$root.classList.remove('slider-inactive');
          this.current = clamp(this.current, 0, this.slideCount - 1);
          this.physicalIndex = this.current;
          // Reset inline styles set during inactive state
          if (this.$track) {
            this.$track.style.justifyContent = '';
            this.$track.style.transform = '';
          }
        }

        if (this.opts.infinite) {
          this.destroyClones();
          this.buildClones();
        }

        this.updateDimensions();

        // Reset animating flag so goTo never silently bails after a mid-resize transition
        this.animating = false;
        this.goTo(this.current, false);

        this.buildDots();
        // Rebuild arrows if transitioning from inactive (they were never built)
        this.buildArrows();
        this.bindExternalArrows();
        this.updateArrows();
        if (wasInactive) this.buildScrollbar();

        if (this.$thumbs) {
          this.$thumbs.remove();
          this.$thumbs = null;
        }
        this.buildThumbs();
        this.updateThumbsActive();
        this.updateDots();

        if (wasInactive) this.startAutoplay();
      },
    }));
  });
}
